import "./BotModuleTester.scss"
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {FormattedMessage, useIntl} from "react-intl";
import { SendHorizontal, RotateCcw, Info } from "lucide-react";
import {PostAssistant, PostAssistantWithId, GetAssistant, AddMessageToThread, CreateAndPollThread, CreateThread, GetThreadMessages, CreateVectorStore, AddFileToVectorStore, RemoveFileFromVectorStore, GetVectorStoreFiles, GetGeminiProject, GetGeminiLocation} from "../../../api/APIBot";
import {GeminiGenerateContent, RetrieveContexts} from "../../../api/APIGemini";
import {useSupportBotContext, useSupportBotData} from "../SupportBotData";
import {AutoResizeTextarea} from "../../../UIElements";
import {useLanguageData} from "../../../App";
import {processSingleBotInstructions, replaceInstructionVariables, mergeGeminiTemplates, getLanguageDisplayName} from "../../../utils/assistantUtils";
import {syncVectorStoreFiles, ensureFileSearchTool} from "../../../utils/vectorStoreUtils";
import {ensureRagRetrievalTool} from "../../../utils/ragCorpusUtils";
import {buildGeminiRequestBody} from "../../../utils/geminiConfigUtils";
import {getTopicDisplayName, getTopicDisplayNameFromTranslations} from "../../../utils/topicUtils";
import {GetGeneralTranslations} from "../../../api/APISurvey";
import {formatTranslationsToIntl} from "../../../Formats";
import ConversationToneToggle, {getDefaultToneForSegment} from "./ConversationToneToggle";
import {useCustomerSegmentData} from "../../../App";

export default function BotModuleTester({ botData, updateRootData }) {
    const intl = useIntl();
    const {
        aiProvider,
        data: {
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            geminiConfig,
            supportTopicCodes
        } = {},
    } = useSupportBotData();

    const isGemini = aiProvider === "gemini";

    const { customerSegment } = useCustomerSegmentData();
    const toneKeys = geminiConfig?.tones ? Object.keys(geminiConfig.tones) : [];
    const [conversationTone, setConversationTone] = useState(
        () => getDefaultToneForSegment(customerSegment)
    );

    const extractRelevantConfig = (data) => {
        if (!data?.config) return null;
        return {
            generalInstructions: data.config.generalInstructions || "",
            topicData: data.config.topicData || {},
            supportTopics: data.config.supportTopics || [],
            materials: data.config.materials || [],
            corpora: data.config.corpora || {},
            supportRequestTarget: data.config.supportRequestTarget || "",
            name: data.config.name || "",
            version: data.config.version || ""
        };
    };

    const [messageContent, setMessageContent] = useState("");
    const [messages, setMessages] = useState([]);
    const [assistantId, setAssistantId] = useState(
        isGemini ? null : (botData?.assistantid || null)
    );
    const [threadId, setThreadId] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const toolEventHistoryRef = useRef({});
    const messagesEndRef = useRef(null);

    const [lastSyncedConfig, setLastSyncedConfig] = useState(() => extractRelevantConfig(botData));
    const [configChanged, setConfigChanged] = useState(false);

    // OpenAI hooks
    const postAssistant = PostAssistant();
    const postAssistantWithId = PostAssistantWithId(assistantId);
    const getAssistant = GetAssistant(assistantId, !isGemini && !!assistantId);

    const createThread = CreateThread();
    const addMessageToThread = AddMessageToThread(threadId);
    const runAndPoll = CreateAndPollThread(threadId);
    const getThreadMessages = GetThreadMessages(threadId);

    // OpenAI vector store hooks
    const createVectorStore = CreateVectorStore();
    const addFileToVectorStore = AddFileToVectorStore();
    const removeFileFromVectorStore = RemoveFileFromVectorStore();
    const { refetch: refetchVectorStoreFiles } = GetVectorStoreFiles(
        botData?.vector_store_id,
        false
    );

    // Gemini hooks
    const geminiGenerateContent = GeminiGenerateContent();
    const retrieveContexts = RetrieveContexts();
    const geminiProjectQuery = GetGeminiProject();
    const geminiLocationQuery = GetGeminiLocation();


    const {instanceLang} = useLanguageData()
    const botLanguage = instanceLang || "en"
    const botLangTranslations = GetGeneralTranslations(botLanguage)

    // Sync assistantId with parent's botData (assistantid for OpenAI, aiagent for Gemini)
    useEffect(() => {
        const parentId = isGemini ? (botData?.aiagent || null) : (botData?.assistantid || null);
        if (parentId !== assistantId) {
            setAssistantId(parentId);
        }
    }, [botData?.assistantid, botData?.aiagent, isGemini]);

    // Detect configuration changes
    useEffect(() => {
        const currentConfig = extractRelevantConfig(botData);

        if (lastSyncedConfig && currentConfig) {
            const hasChanged = !_.isEqual(lastSyncedConfig, currentConfig);
            setConfigChanged(hasChanged);
        }
    }, [botData, lastSyncedConfig]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleReset = async () => {
        if (isSending) return;

        // Clear all state
        setMessages([]);
        setThreadId(null);
        toolEventHistoryRef.current = {};
        setMessageContent("");
        setConfigChanged(false);

        // Ensure assistant is ready for new conversation (OpenAI only)
        if (!isGemini) {
            try {
                const vectorStoreId = await handleTestVectorStore();
                await ensureAssistantReady(true, vectorStoreId);
            } catch (err) {
                console.error("Failed to reset assistant:", err);
            }
        }
    };

    // Sync vector store files for testing (OpenAI only — Gemini uses shared Knowledge Bases)
    const handleTestVectorStore = async () => {
        try {
            if (isGemini) {
                // Gemini bots use Knowledge Bases managed via Bot Competences
                return null;
            }

            const vectorStoreId = await syncVectorStoreFiles({
                botData: botData,
                updatedBotData: botData,
                createVectorStoreFn: createVectorStore.mutateAsync,
                addFileFn: addFileToVectorStore.mutateAsync,
                removeFileFn: removeFileFromVectorStore.mutateAsync,
                refetchFilesFn: refetchVectorStoreFiles
            });

            if (vectorStoreId) {
                botData.vector_store_id = vectorStoreId;
            }

            if (vectorStoreId && botData.id !== 'new') {
                updateRootData("vector_store_id", vectorStoreId);
            }

            return vectorStoreId;
        } catch (error) {
            console.error("Error syncing test vector store/corpus:", error);
            throw error;
        }
    };

    const buildAssistantPostBody = useMemo(() => {
        if (!assistantInstructionsConfig?.postBody || !botData?.config) return null;

        const processed = replaceInstructionVariables(
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            botLanguage
        );

        // For Gemini, override templates with Gemini-specific ones
        if (isGemini) {
            mergeGeminiTemplates(processed, geminiConfig);
        }

        // Name: bot name + (id) or [TEST] for new bots
        const botId = botData.id === 'new' ? '[TEST]' : botData.id;
        processed.postBody.name = `${botData.config.name || "SupportBot"} (${botId})`;

        // Resolve taxonomy titles in the bot language (not the admin UI language)
        const botTranslationMap = botLangTranslations?.data
            ? formatTranslationsToIntl(botLangTranslations.data)
            : null;
        const mergedTopicData = {};
        (botData.config.supportTopics || []).forEach(code => {
            const data = botData.config.topicData?.[code] || supportTopicCodes?.[code] || {};
            mergedTopicData[code] = {
                ...data,
                title: botTranslationMap
                    ? getTopicDisplayNameFromTranslations(code, data, botTranslationMap)
                    : getTopicDisplayName(code, data, intl)
            };
        });

        const botSection = processSingleBotInstructions({
            bot: botData,
            processedConfig: processed,
            intl,
            categoryData: mergedTopicData,
            translationMap: botTranslationMap
        });

        // Merge into main instructions (use Gemini-specific master prompt if available)
        const masterPrompt = (isGemini && geminiConfig?.systemInstructionTemplate)
            ? geminiConfig.systemInstructionTemplate
            : (processed.postBody.instructions || "");
        const tones = geminiConfig?.tones;
        const communicationStyle = (isGemini && tones && tones[conversationTone])
            ? tones[conversationTone]
            : '';

        // Single module: the only valid support request target is this module's, if any.
        let supportTargets = processed.supportTargetsNone || '';
        const targetCode = botData.config?.supportRequestTarget;
        if (targetCode) {
            const name = botTranslationMap?.['role.' + targetCode];
            supportTargets = name && name !== targetCode ? `- ${targetCode} (${name})` : `- ${targetCode}`;
        }

        processed.postBody.instructions = masterPrompt
            .replace(/{botInstructions}/g, botSection.trim())
            .replace(/{initialMessage}/g, "")
            .replace(/{communicationStyle}/g, communicationStyle)
            .replace(/{supportTargets}/g, supportTargets)
            .replace(/{defaultLanguage}/g, getLanguageDisplayName(botLanguage));

        if (isGemini) {
            // Gemini tools come from geminiConfig.tools — only add RAG tool here
            processed.postBody.tools = ensureRagRetrievalTool(
                [],
                botData.config?.corpora || {}
            );
            return processed.postBody;
        }

        // OpenAI: ensure file_search tool is present if there are materials
        processed.postBody.tools = ensureFileSearchTool(
            processed.postBody.tools,
            botData.config.materials
        );

        return processed.postBody;
    }, [assistantInstructionsConfig, botData, intl, supportBotInstructions, supportTopicInstructionBody, supportBotInstructionBody, botLanguage, isGemini, botLangTranslations?.data, conversationTone]);

    const ensureAssistantReady = async (forceUpdate = false, vectorStoreId = null) => {
        if (!buildAssistantPostBody) throw new Error("Assistant config missing");

        // Gemini: no per-module aiagent needed — tester calls generateContent directly
        if (isGemini) return null;

        // --- OpenAI: existing flow ---
        // Update existing assistant
        if (assistantId) {
            // Skip update if no force update and config hasn't changed
            if (!forceUpdate && !configChanged) {
                return assistantId;
            }

            if (getAssistant.isLoading) {
                let attempts = 0;
                while (attempts < 30 && getAssistant.isLoading) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((r) => setTimeout(r, 200));
                    attempts += 1;
                }
            }

            if (getAssistant.isSuccess && getAssistant.data) {
                const postBody = _.cloneDeep(buildAssistantPostBody);

                if (vectorStoreId) {
                    postBody.tool_resources = {
                        file_search: {
                            vector_store_ids: [vectorStoreId]
                        }
                    };
                } else if (getAssistant.data.tool_resources) {
                    postBody.tool_resources = getAssistant.data.tool_resources;
                }

                const res = await postAssistantWithId.mutateAsync(postBody);
                if (res?.data?.id) {
                    setAssistantId(res.data.id);
                    if (botData.id !== 'new') {
                        updateRootData("assistantid", res.data.id)
                    }
                    setLastSyncedConfig(extractRelevantConfig(botData));
                    setConfigChanged(false);
                }
                return res?.data?.id || assistantId;
            }

            if (getAssistant.isError) {
                // fall through to a create as safety
            }
        }

        // Create new
        const postBody = _.cloneDeep(buildAssistantPostBody);

        if (vectorStoreId) {
            postBody.tool_resources = {
                file_search: {
                    vector_store_ids: [vectorStoreId]
                }
            };
        }

        const createRes = await postAssistant.mutateAsync(postBody);
        const newId = createRes?.data?.id;
        if (!newId) throw new Error("Assistant creation failed");
        setAssistantId(newId);
        if (botData.id !== 'new') {
            updateRootData("assistantid", newId)
        }
        setLastSyncedConfig(extractRelevantConfig(botData));
        setConfigChanged(false);
        return newId;
    };

    const rebuildMessagesWithToolEvents = (apiMessages, currentToolEvents = null) => {
        const finalMessages = [];
        let userMessageIndex = 0;

        for (let i = 0; i < apiMessages.length; i++) {
            const msg = apiMessages[i];

            if (msg.role === 'user') {
                userMessageIndex++;
                finalMessages.push(msg);

                // Check if we have tool events for this user message
                const storedEvents = toolEventHistoryRef.current[userMessageIndex];
                const isLastUserMessage = i === apiMessages.map((m, idx) => m.role === 'user' ? idx : -1).filter(idx => idx >= 0).pop();

                // Add tool events after this user message
                if (isLastUserMessage && currentToolEvents && currentToolEvents.length > 0) {
                    // This is the current interaction, add new tool events
                    finalMessages.push(...currentToolEvents.map(e => ({
                        role: "system",
                        content: e
                    })));
                } else if (storedEvents) {
                    // Add previously stored tool events
                    finalMessages.push(...storedEvents.map(e => ({
                        role: "system",
                        content: e
                    })));
                }
            } else {
                finalMessages.push(msg);
            }
        }

        return finalMessages;
    };

    const textareaRef = useRef(null);

    // Build Gemini conversation history from messages state
    const buildGeminiContents = (currentMessages) => {
        return currentMessages
            .filter(m => m.role === "user" || m.role === "assistant" || m.role === "model")
            .map(m => ({
                role: m.role === "assistant" ? "model" : m.role,
                parts: [{ text: m.content || "" }]
            }));
    };

    const handleSendMessage = async (e) => {
        e?.preventDefault?.();
        if (!messageContent.trim() || isSending) return;

        setIsSending(true);
        setIsLoading(true);
        const userText = messageContent;
        setMessageContent("");
        textareaRef.current?.focus();

        // Add user message and loading indicator immediately
        const userMessage = { role: "user", content: userText };
        const loadingMessage = {
            role: "assistant",
            content: "",
            isLoading: true
        };

        setMessages((prev) => [...prev, userMessage, loadingMessage]);

        try {
            // Sync vector store before testing (OpenAI only)
            const vectorStoreId = isGemini ? null : await handleTestVectorStore();

            // Update assistant only if configuration has changed or doesn't exist yet (OpenAI only)
            const readyAssistantId = isGemini ? null : await ensureAssistantReady(false, vectorStoreId);

            if (isGemini) {
                // --- Gemini: client-side conversation with function calling loop ---
                const postBody = _.cloneDeep(buildAssistantPostBody);

                const currentMessages = messages.filter(m => !m.isLoading && m.role !== "system");
                const contents = [
                    ...buildGeminiContents(currentMessages),
                    { role: "user", parts: [{ text: userText }] }
                ];

                const { model: geminiModel, requestBody } = buildGeminiRequestBody(
                    geminiConfig,
                    postBody.instructions,
                    contents,
                    postBody.tools || [],
                    { botData, supportTopicCodes }
                );

                // Substitute {currentDate} in system instruction
                const currentDate = new Date().toISOString().slice(0, 10);
                requestBody.systemInstruction?.parts?.forEach(part => {
                    if (part.text) {
                        part.text = part.text.replace(/\{currentDate}/g, currentDate);
                    }
                });

                const MAX_ROUNDS = 5;
                const allToolEvents = [];
                let finalText = "";

                const project = geminiProjectQuery?.data?.[0]?.value;
                const location = geminiLocationQuery?.data?.[0]?.value;
                const ragConfig = geminiConfig?.ragConfig;
                const corpora = botData.config?.corpora || {};

                for (let round = 0; round < MAX_ROUNDS; round++) {
                    const currentRequestBody = round === 0
                        ? requestBody
                        : { ...requestBody, contents };

                    // eslint-disable-next-line no-await-in-loop
                    const response = await geminiGenerateContent.mutateAsync({
                        model: geminiModel,
                        requestBody: currentRequestBody
                    });

                    const { toolEvents, responseText } = normalizeGeminiResponse(response);

                    if (toolEvents.length === 0) {
                        finalText = responseText;
                        break;
                    }

                    // Execute all function calls in parallel
                    // eslint-disable-next-line no-await-in-loop
                    const results = await Promise.all(
                        toolEvents.map(event =>
                            executeFunctionCall(event, {
                                corpora,
                                project,
                                location,
                                ragConfig,
                                retrieveContextsFn: retrieveContexts.mutateAsync
                            })
                        )
                    );

                    // Enrich tool events with result metadata for UI display
                    toolEvents.forEach((event, i) => {
                        event.resultCount = results[i].resultCount;
                    });

                    allToolEvents.push(...toolEvents);

                    // Append model's function call turn
                    contents.push({
                        role: "model",
                        parts: response.candidates?.[0]?.content?.parts || []
                    });

                    // Append function responses
                    contents.push({
                        role: "user",
                        parts: results.map((r, i) => ({
                            functionResponse: {
                                name: toolEvents[i].name,
                                response: { result: r.text }
                            }
                        }))
                    });

                    // If this is the last allowed round, use whatever text we got
                    if (round === MAX_ROUNDS - 1) {
                        finalText = responseText || "Max function calling rounds reached.";
                    }
                }

                // Store tool events for this interaction
                if (allToolEvents.length > 0) {
                    const userMessageCount = currentMessages.filter(m => m.role === 'user').length + 1;
                    toolEventHistoryRef.current[userMessageCount] = allToolEvents;
                }

                setMessages(prev => {
                    const withoutLoading = prev.filter(m => !m.isLoading);
                    return [
                        ...withoutLoading,
                        ...allToolEvents.map(e => ({ role: "system", content: e })),
                        { role: "assistant", content: finalText }
                    ];
                });
            } else {
                // --- OpenAI: existing server-side thread flow ---
                // Thread
                let tId = threadId;
                if (!tId) {
                    const t = await createThread.mutateAsync();
                    tId = t?.id;
                    if (!tId) throw new Error("Thread creation failed");
                    setThreadId(tId);
                }

                // Add message to thread
                await addMessageToThread.mutateAsync({ role: "user", content: userText });

                // Run + Poll (also yields any tool call stubs to render)
                const { toolEvents } = await runAndPoll.mutateAsync({ assistantId: readyAssistantId });

                // Store tool events for this interaction
                if (toolEvents && toolEvents.length > 0) {
                    const latest = await getThreadMessages.refetch();
                    const apiMessages = normalizeMessages(latest?.data?.data || []);
                    const userMessageCount = apiMessages.filter(m => m.role === 'user').length;
                    toolEventHistoryRef.current[userMessageCount] = toolEvents;
                }

                // Fetch latest thread messages
                const latest = await getThreadMessages.refetch();
                const apiMessages = normalizeMessages(latest?.data?.data || []);

                // Rebuild messages with all tool events properly placed
                const finalMessages = rebuildMessagesWithToolEvents(apiMessages, toolEvents);

                setMessages(finalMessages);
            }
        } catch (err) {
            console.error(err);

            // Remove the loading message and add error
            setMessages((prev) => {
                const withoutLoading = prev.filter(m => !m.isLoading);
                return [
                    ...withoutLoading,
                    {
                        role: "system",
                        content: intl.formatMessage({
                            defaultMessage: "Pahoittelut. Jotain meni pieleen.",
                            id: "botModuleTester.error",
                        }),
                    },
                ];
            });
        } finally {
            setIsSending(false);
            setIsLoading(false);
        }
    };

    return (
        <div className={"module-tester"}>
            <div className={"module-header"}>
                <h2>
                    {intl.formatMessage({
                        defaultMessage: "Test module",
                        id: "testBot",
                    })}
                    <button
                        className={"reset-button"}
                        onClick={handleReset}
                        disabled={isSending}
                        title={intl.formatMessage({
                            defaultMessage: "Reset conversation",
                            id: "resetConversation",
                        })}
                    >
                        <RotateCcw size={18} />
                    </button>
                </h2>
            </div>

            <div className={"messages"}>
                {isGemini && messages.length > 0 && (
                    <span className={"tone-indicator"}>
                        <span className={"tone-header"}>
                                    {intl.formatMessage({
                                        id: 'conversationTone',
                                        defaultMessage: 'Conversation tone',
                                    })}{": "}
                                </span>
                        {intl.formatMessage(
                            conversationTone === "casual"
                                ? { id: "bot.tone.casual", defaultMessage: "Casual" }
                                : { id: "bot.tone.professional", defaultMessage: "Professional" }
                        )}
                    </span>
                )}
                {messages.length > 0 ? (
                    <div className="messages-content">
                        {messages.map((m, i) => (
                            <div key={i} className={`msg msg--${m.role}${m.isLoading ? ' msg--loading' : ''}`}>
                                {
                                    m.isLoading ?
                                        <span className={"dot"}></span> :
                                        m.role === "system" ?
                                            <ToolEventComponent event={m.content} botData={botData} /> :
                                            <TextWithLinks text={m.content.trim()} />
                                }
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                ) : (
                    <div className={"messages-placeholder"}>
                        {isGemini && (
                            <>
                                <span className={"tone-header"}>
                                    {intl.formatMessage({
                                        id: 'conversationTone',
                                        defaultMessage: 'Conversation tone',
                                    })}
                                </span>
                                <ConversationToneToggle
                                    tone={conversationTone}
                                    onChange={setConversationTone}
                                    toneKeys={toneKeys}
                                />
                            </>
                        )}
                        <p>
                            {intl.formatMessage({
                                defaultMessage: "Start testing by sending a message.",
                                id: "moduleTestingMessagesPlaceholder",
                            })}
                        </p>
                    </div>
                )}
            </div>

            {configChanged && messages.length > 0 && (
                <div className="config-change-notice">
                    <div className={"config-change-notice--container"}>
                        <Info size={18} />
                        <span>
                            {intl.formatMessage({
                                defaultMessage: "Module instructions have changed",
                                id: "botModuleTester.configChanged",
                            })}
                        </span>
                        <button
                            onClick={handleReset}
                            disabled={isSending}
                            className="reset-inline-btn"
                        >
                            {intl.formatMessage({
                                defaultMessage: "Empty conversation",
                                id: "emptyConversation",
                            })}
                        </button>
                    </div>
                </div>
            )}

            <div className={"input-container"}>
                <form onSubmit={handleSendMessage}>
                    <AutoResizeTextarea
                        ref={textareaRef}
                        rows={1}
                        maxRows={5}
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        placeholder={intl.formatMessage({
                            defaultMessage: "Type message...",
                            id: "testModuleInputPlaceholder",
                        })}
                        submitOnEnter={true}
                        onSubmit={handleSendMessage}
                    />
                    <button type={"submit"} disabled={isSending || !buildAssistantPostBody}>
                        <SendHorizontal size={20} />
                    </button>
                </form>
            </div>
        </div>
    );
}

const URL_SPLIT_REGEX = /(https?:\/\/[^\s<>"']+)/g;
const URL_TEST_REGEX = /^https?:\/\//;

function TextWithLinks({ text }) {
    const parts = text.split(URL_SPLIT_REGEX);
    return (
        <>
            {parts.map((part, i) =>
                URL_TEST_REGEX.test(part) ? (
                    <a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
                ) : (
                    part
                )
            )}
        </>
    );
}

function ToolEventComponent({event, botData}) {
    const { data: { supportTopicCodes } } = useSupportBotContext();
    const getTopicTitle = (topicId) => {
        const topicData = botData.config?.topicData?.[topicId] || supportTopicCodes?.[topicId];
        return getTopicDisplayName(topicId, topicData, intl);
    };
    const intl = useIntl()
    if (event?.name === "create_supportneed") {
        const supportTopic = getTopicTitle(event?.args?.category)
        return <FormattedMessage
            id={"supportRequestCreated.moduleTester"}
            defaultMessage={'Annie created a support request {supportTopic}{br}with note: "{note}"'}
            values={{br: <br/>, supportTopic: <b>{supportTopic}</b>, note: <em>{event?.args?.description}</em>}}/>
    }
    if (event?.name === "search_knowledge_base") {
        const source = event?.args?.source || "knowledge base";
        const query = event?.args?.query || "";
        const resultCount = event?.resultCount;
        return <>
            <FormattedMessage
                id={"ragSearchPerformed.moduleTester"}
                defaultMessage={'Annie searched {source} for "{query}"'}
                values={{
                    source: <b>{source}</b>,
                    query: query,
                }}/>
            {resultCount != null && <>{' — '}<FormattedMessage
                id={"ragSearchResults.moduleTester"}
                defaultMessage={'found {resultCount} results'}
                values={{ resultCount }}
            /></>}
        </>;
    }
    return <>{intl.formatMessage({id: 'functionCalled.moduleTester', defaultMessage: 'Annie called a function.'})}</>
}

function normalizeMessages(apiMessages = []) {
    // Converts OpenAI Assistants v2 style messages into [{role, content}] flat list
    // Also keeps any earlier user/system messages already in UI coherent if caller merges.
    const simplified = apiMessages
        .slice()
        .sort((a, b) => a.created_at - b.created_at)
        .map((m) => {
            const role = m.role || "assistant";
            // Text first; if no text, stringify content for visibility
            let text = "";
            if (Array.isArray(m.content) && m.content.length > 0) {
                const block = m.content.find((c) => c.type === "text");
                if (block?.text?.value) text = block.text.value;
                else text = safeStringify(m.content);
            } else if (m.content?.[0]?.text?.value) {
                text = m.content[0].text.value;
            } else {
                text = safeStringify(m.content || m);
            }
            // Hide citations-looking tokens like 【...】
            text = text.replace(/【[^】]+】/g, "");
            return { role, content: text };
        });

    return simplified;
}

function safeStringify(obj) {
    try {
        return typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
    } catch {
        return String(obj ?? "");
    }
}

/**
 * Normalizes a Gemini generateContent response into text and tool events
 * @param {Object} response - Gemini API response
 * @returns {{ responseText: string, toolEvents: Array }}
 */
function normalizeGeminiResponse(response) {
    const toolEvents = [];
    let responseText = "";

    const parts = response?.candidates?.[0]?.content?.parts || [];

    for (const part of parts) {
        if (part.text) {
            responseText += part.text;
        }
        if (part.functionCall) {
            toolEvents.push({
                name: part.functionCall.name,
                args: part.functionCall.args || {},
                id: part.functionCall.name + "_" + Date.now()
            });
        }
    }

    return { responseText, toolEvents };
}

/**
 * Routes a function call to the appropriate handler.
 * Returns { text: string, resultCount: number|null }.
 */
async function executeFunctionCall(event, { corpora, project, location, ragConfig, retrieveContextsFn }) {
    const { name, args } = event;

    switch (name) {
        case 'search_knowledge_base':
            return await handleSearchKnowledgeBase(args, { corpora, project, location, ragConfig, retrieveContextsFn });
        case 'create_supportneed':
            return { text: 'Support need created successfully.', resultCount: null };
        default:
            return { text: `Error: Unknown function "${name}".`, resultCount: null };
    }
}

/**
 * Calls the Vertex AI retrieveContexts API and formats results.
 */
async function handleSearchKnowledgeBase(args, { corpora, project, location, ragConfig, retrieveContextsFn }) {
    const { query, source } = args;

    // Resolve source slug to corpus ID
    let corpusId;
    if (source && corpora[source]) {
        corpusId = corpora[source];
    } else if (!source && Object.keys(corpora).length > 0) {
        corpusId = Object.values(corpora)[0];
    } else {
        const errorText = source
            ? `Unknown RAG source: ${source}`
            : 'RAG search is not available.';
        return { text: errorText, resultCount: 0 };
    }

    if (!project || !location) {
        return { text: 'Error: Gemini project or location not configured.', resultCount: 0 };
    }

    const corpusPath = `projects/${project}/locations/${location}/ragCorpora/${corpusId}`;
    const topK = ragConfig?.topK ?? 10;
    const vectorDistanceThreshold = ragConfig?.vectorDistanceThreshold ?? 0.5;

    const requestBody = {
        vertex_rag_store: {
            rag_resources: [{ rag_corpus: corpusPath }]
        },
        query: {
            text: query,
            rag_retrieval_config: {
                top_k: topK,
                ...(vectorDistanceThreshold !== null
                    ? { filter: { vector_distance_threshold: vectorDistanceThreshold } }
                    : {})
            }
        }
    };

    try {
        const response = await retrieveContextsFn(requestBody);
        return formatRagResults(response);
    } catch (error) {
        console.error('RAG retrieval error:', error);
        return { text: `Error: RAG search failed — ${error.message}`, resultCount: 0 };
    }
}

/**
 * Formats retrieveContexts API response into a text summary.
 */
function formatRagResults(response) {
    const contexts = response?.contexts?.contexts || [];

    if (contexts.length === 0) {
        return { text: 'The search produced no results.', resultCount: 0 };
    }

    let result = `Found ${contexts.length} results:\n\n`;
    contexts.forEach((ctx, i) => {
        result += `${i + 1}. ${ctx.text}\n`;
        if (ctx.sourceUri) {
            result += `   Source: ${ctx.sourceUri}`;
            if (ctx.score !== undefined) {
                result += ` (relevance: ${ctx.score.toFixed(3)})`;
            }
            result += '\n';
        }
        result += '\n';
    });

    return { text: result.trim(), resultCount: contexts.length };
}